import React from 'react';
import { Link } from 'react-router-dom';
import ArticlePage from './ArticlePage';

function DecisionIntelligencePage() {
  const articleContent = (
    <>
      <h2>The Hidden Failure Mode in Modern Operations</h2>
      <p>
        Most organizations have invested heavily in visibility.
      </p>
      <p>
        They have dashboards.
        They have KPIs.
        They have data pipelines and predictive models.
      </p>
      <p>
        And yet execution still breaks down.
      </p>
      <p>
        Projects slip.
        <Link to="/solutions/revenue-pipeline" className="text-gray-900 underline hover:text-gray-600">Revenue leaks across handoffs</Link>.
        Teams optimize locally and hurt the system globally.
        Decisions look right in reports — and fail in reality.
      </p>
      <p>
        The issue isn't lack of intelligence.
      </p>
      <p>
        It's that most tools describe <strong>what is happening</strong>, not <strong>how the system will behave when decisions change</strong>.
      </p>

      <h2>The Shift: From Seeing Problems to Stress-Testing Decisions</h2>
      <p>
        Operational Intelligence answers:
      </p>
      <ul>
        <li>Where are we inefficient?</li>
        <li>Where are bottlenecks forming?</li>
        <li>Where is execution breaking down?</li>
      </ul>
      <p>
        Decision Intelligence must answer:
      </p>
      <ul>
        <li>If we fix this bottleneck, what new constraint appears?</li>
        <li>If we accelerate growth, what breaks downstream?</li>
        <li>If we cut cost here, what risk accumulates elsewhere?</li>
        <li>Which intervention creates the highest leverage over time?</li>
      </ul>
      <p>
        In real operations, every fix changes the system.
      </p>

      <h2>Why Dashboards and Process Maps Plateau</h2>
      <p>
        Dashboards show metrics.
        Process maps show sequence.
        Forecasts show likely outcomes.
      </p>
      <p>
        But real organizations are driven by:
      </p>
      <ul>
        <li><Link to="/innovation/feedback-loops" className="text-gray-900 underline hover:text-gray-600">Feedback loops</Link> between teams</li>
        <li>Delays between action and impact</li>
        <li>Human decision behavior</li>
        <li>Policy and incentive structures</li>
        <li>Shared resource constraints</li>
      </ul>
      <p>
        This is why companies repeatedly:
      </p>
      <ul>
        <li>Solve yesterday's bottleneck</li>
        <li>Create tomorrow's constraint</li>
        <li>Chase performance variability instead of stabilizing systems</li>
      </ul>

      <div className="my-8">
        <img 
          src="/images/decision-intelligence-square.png" 
          alt="The New Operational Stack" 
          className="w-full h-auto rounded-lg"
          loading="lazy"
        />
      </div>

      <h2>The New Operational Stack</h2>
      <p>
        Modern operational transformation is moving toward four layers:
      </p>
      <p>
        <strong>1. Observability</strong><br />
        Shows what happened and what is happening.
      </p>
      <p>
        <strong>2. Operational Intelligence</strong><br />
        Shows where execution breaks down.
      </p>
      <p>
        <strong>3. System Behavior Modeling</strong><br />
        Shows how the system responds when policies or resources change.
      </p>
      <p>
        <strong>4. Decision Intelligence</strong><br />
        Shows which decisions hold up under real-world pressure.
      </p>
      <p>
        Most organizations stop at Layer 2.
        Competitive advantage increasingly lives in Layers 3 and 4.
      </p>

      <h2>Where This Matters Most: SMB and Mid-Market Execution</h2>
      <p>
        Large enterprises can absorb inefficiency.
        SMBs and mid-market companies usually cannot.
      </p>
      <p>
        In these environments:
      </p>
      <ul>
        <li>One broken handoff can destroy margin</li>
        <li>One overloaded team can <Link to="/solutions/workforce" className="text-gray-900 underline hover:text-gray-600">stall growth</Link></li>
        <li>One misaligned KPI can create cross-functional conflict</li>
      </ul>
      <p>
        These organizations don't need more reporting.
      </p>
      <p>
        They need <strong>clear, testable operational decision systems</strong>.
      </p>

      <h2>What High-Performing Operational Organizations Do Differently</h2>
      <p>
        They don't just document processes. They:
      </p>
      <ul>
        <li>Test decisions before rollout</li>
        <li>Model cross-functional impacts</li>
        <li>Identify leverage points instead of symptoms</li>
        <li>Stress test policies before committing capital or headcount</li>
        <li>Align leadership around system behavior, not department metrics</li>
      </ul>

      <h2>The Next Evolution of Operational Leadership</h2>
      <p>
        The next generation of operational leaders won't be defined by:
      </p>
      <ul>
        <li>How much data they have</li>
        <li>How advanced their dashboards are</li>
        <li>How many AI tools they deploy</li>
      </ul>
      <p>
        They will be defined by:
      </p>
      <ul>
        <li>How well they <Link to="/innovation/system-dynamics" className="text-gray-900 underline hover:text-gray-600">understand system behavior</Link></li>
        <li>How safely they can test decisions</li>
        <li>How clearly they can explain tradeoffs</li>
        <li>How confidently they can <Link to="/innovation/uncertainty" className="text-gray-900 underline hover:text-gray-600">act under uncertainty</Link></li>
      </ul>

      <h2>The End State</h2>
      <p>
        Operational Intelligence helped organizations see.
      </p>
      <p>
        Decision Intelligence helps them choose — and choose in ways that hold up across teams, time, and changing conditions.
      </p>
      <p>
        Fewer blind spots.
        Better prioritization.
        Decisions that last under pressure.
      </p>
    </>
  );

  const relatedArticles = [
    { category: 'Article', title: 'System Dynamics in Enterprise Decision Making', href: '/innovation/system-dynamics' },
    { category: 'Article', title: 'Feedback Loops in Business Strategy', href: '/innovation/feedback-loops' },
    { category: 'Article', title: 'Quantifying Uncertainty in Strategic Models', href: '/innovation/uncertainty' },
  ];

  return (
    <ArticlePage
      category="Article"
      title="From Operational Intelligence to Decision Intelligence"
      subtitle="Closing the gap between visibility and outcomes—how to transform data awareness into strategic action."
      heroImage="/images/decision-intelligence-hero.png"
      articleContent={articleContent}
      relatedArticles={relatedArticles}
    />
  );
}

export default DecisionIntelligencePage;
